import classnames from 'classnames'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'

import { cn } from '@repo/utils'

import { isAdmin, isBot } from 'app'
import { ProgressBar, SimpleButton } from 'ui'

import StageEdit from 'tournaments/src/components/StageEdit'

import { IMPORT_TYPES_NAMES, STAGES_TYPES_NAMES } from '../../constants'
import { actions, selectors } from '../../redux'
import { ImportTypes } from '../../schemas/StageSchema'
import { DeleteRoundButton } from './DeleteRoundButton'

const Stages = () => {
  const dispatch = useDispatch()
  const params = useParams<{ id: string }>()
  const { key, data, loading: listLoading } = useSelector(selectors.stages.list)
  const { loading: tournamentLoading } = useSelector(selectors.tournament)
  const user = useSelector(({ auth }: { auth: { usr?: { email: string } } }) => auth.usr)
  const [isAdding, setIsAdding] = useState(false)
  const [editStageId, setEditStageId] = useState<null | string>(null)
  const isBotUser = isBot(user?.email || '')
  const tournamentId = params.id || ''

  const loading = listLoading || tournamentLoading

  useEffect(() => {
    if (key !== params.id) {
      dispatch(actions.stagesList({ tournamentId }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onUpdateList = () => {
    setIsAdding(false)
    setEditStageId(null)
    dispatch(actions.stagesList({ tournamentId }))
  }

  const onUpdateStats = () => {
    dispatch(actions.updateStats({ tournamentId }))
  }

  return (
    <div className="Stages flex flex-col gap-2 *:bg-white">
      {loading && <ProgressBar value={100} />}
      {data.map((stage) => (
        <div key={stage.id} className="border rounded overflow-hidden">
          <div className="flex gap-1">
            <b className="border-r p-2 w-10 text-right">{stage.order + 1}</b>
            {editStageId === stage.id ? (
              <div className="grow">
                <StageEdit
                  maxOrder={data.length}
                  onCancel={() => setEditStageId(null)}
                  onSave={onUpdateList}
                  stage={stage}
                  tournamentId={stage.tournament_id}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-start grow gap-1 px-1 py-2 overflow-hidden">
                <div className="flex items-start gap-2">
                  <div className="font-bold truncate">{stage.name}</div>
                  <div
                    className={classnames(
                      'px-2 rounded-md border border-current text-xs truncate',
                      stage.is_filled && !stage.is_ended && 'text-blue',
                      stage.is_filled && stage.is_ended && 'text-green',
                      !stage.is_filled && !stage.is_ended && 'text-orange',
                    )}
                  >
                    {STAGES_TYPES_NAMES[stage.type]}
                  </div>
                </div>
                <div className="flex flex-wrap justify-start gap-2 text-sm">
                  <div className="flex border border-greyLight rounded">
                    <div className="py-1 px-2 border-r bold">Grupos</div>
                    <div className="flex items-center py-1 px-2 font-bold text-sm">{stage.groups_total}</div>
                  </div>
                  <div className="flex border border-greyLight rounded">
                    <div className="py-1 px-2 border-r bold">Equipos</div>
                    <div className="flex items-center py-1 px-2 font-bold text-sm">{stage.teams_total}</div>
                  </div>
                  <div className="flex border border-greyLight rounded">
                    <div className="py-1 px-2 border-r bold">Encuentros</div>
                    <div className="flex items-center py-1 px-2 font-bold text-sm">{stage.matches_total}</div>
                  </div>
                  <div className="flex border border-greyLight rounded">
                    <div className="py-1 px-2 border-r bold">Encuentros completados</div>
                    <div className="flex items-center py-1 px-2 font-bold text-sm">{stage.is_filled ? 'Sí' : 'No'}</div>
                  </div>
                  <div className="flex border border-greyLight rounded">
                    <div className="py-1 px-2 border-r bold">Iniciada</div>
                    <div className="flex items-center py-1 px-2 font-bold text-sm">
                      {stage.is_started ? 'Sí' : 'No'}
                    </div>
                  </div>
                  <div className="flex border border-greyLight rounded">
                    <div className="py-1 px-2 border-r bold">Finalizada</div>
                    <div className="flex items-center py-1 px-2 font-bold text-sm">{stage.is_ended ? 'Sí' : 'No'}</div>
                  </div>
                </div>
                {stage.imports.map((imp) => {
                  const codes = imp.url
                    .split(',')
                    .map((c) => c.trim())
                    .filter(Boolean)

                  return (
                    <div key={imp.id}>
                      {isBotUser && (
                        <div className="text-sm py-1">
                          <b>Anterior: </b>
                          {imp.last_run ? (
                            moment.unix(imp.last_run).format('ddd, DD [de] MMM YYYY HH:mm')
                          ) : (
                            <i>Indefinido</i>
                          )}
                          , <b>Siguiente: </b>
                          {imp.next_run ? (
                            moment.unix(imp.next_run).format('ddd, DD [de] MMM YYYY HH:mm')
                          ) : (
                            <i>Indefinido</i>
                          )}
                        </div>
                      )}
                      <div key={imp.id} className="flex flex-wrap justify-start gap-2 text-xs">
                        <div className="flex border border-current text-blueLight rounded py-0.5 px-2">
                          {IMPORT_TYPES_NAMES[imp.type]}
                        </div>
                        {imp.is_working && (
                          <div className="flex border border-current text-orangeLight rounded py-0.5 px-2">
                            Procesando
                          </div>
                        )}
                        {imp.is_active ? (
                          <div className="flex border border-current text-greenLight rounded py-0.5 px-2">Activo</div>
                        ) : (
                          <div className="flex border border-current text-grey rounded py-0.5 px-2">Inactivo</div>
                        )}
                        {isBotUser && codes.length > 0 && (
                          <div className="flex border border-current text-grey rounded py-0.5 px-2">{codes.length}</div>
                        )}
                        {isBotUser &&
                          codes.map((code) => {
                            const codeNumber = code.split('-')[0]?.replace(/\D/g, '')
                            return (
                              <div key={code} className="flex border border-greyLight rounded items-center py-0.5 px-2">
                                {imp.type === ImportTypes.TYPE_LFB && (
                                  <a href={imp.url} rel="noreferrer" target="_blank">
                                    {code}
                                  </a>
                                )}
                                {imp.type === ImportTypes.TYPE_FCF && (
                                  <a
                                    href={`https://fcf.futbol/fpc-comet/fpc/competition_s/competitionsFixtures.php?competition=19780484&subcompetition=${codeNumber}`}
                                    rel="noreferrer"
                                    target="_blank"
                                  >
                                    {code}
                                  </a>
                                )}
                                {imp.type === ImportTypes.TYPE_COMET && (
                                  <a
                                    href={`https://latam.analyticom.de/resources/jsf/competition/index.xhtml?id=${codeNumber}`}
                                    rel="noreferrer"
                                    target="_blank"
                                  >
                                    {code}
                                  </a>
                                )}
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  )
                })}
                <div className="flex justify-start items-center">
                  {isAdmin(stage.access?.level) && (
                    <SimpleButton
                      className="text-xs"
                      disabled={loading}
                      icon="edit"
                      onClick={() => setEditStageId(stage.id)}
                      secondary
                    >
                      Editar fase
                    </SimpleButton>
                  )}
                </div>
                {stage.rounds.length > 0 && (
                  <div className="flex flex-wrap justify-start gap-2">
                    {stage.rounds.map((round) => (
                      <div
                        key={round.id}
                        className={cn('border rounded py-0.5 px-2', round.is_ended && 'bg-greenLight border-green')}
                      >
                        <div className="flex items-start justify-between font-bold border-b">
                          <div>{round.name}</div>
                          <div>{round.matches_count === 0 && <DeleteRoundButton roundId={round.id} />}</div>
                        </div>
                        <div className="text-sm">Encuentros: {round.matches_count}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <ProgressBar ariaLabel="Relevancia de la fase" className="text-xxs" value={stage.relevance} />
        </div>
      ))}
      <div className="border rounded px-2 py-1">
        {!isAdding && (
          <SimpleButton disabled={loading} icon="plus" onClick={() => setIsAdding(true)} primary>
            Agregar nueva fase
          </SimpleButton>
        )}
        {isAdmin(data[0]?.access?.level) && (
          <SimpleButton disabled={loading} icon="line graph" onClick={onUpdateStats} secondary>
            Generar estadísticas
          </SimpleButton>
        )}
      </div>
      {isAdding && (
        <div className="border border-blueLight shadow rounded overflow-hidden">
          <StageEdit
            maxOrder={data.length + 1}
            onCancel={() => setIsAdding(false)}
            onSave={onUpdateList}
            tournamentId={tournamentId}
          />
        </div>
      )}
    </div>
  )
}

export default Stages
